import { useCallback } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { OutboundClient, type OutboundTab } from "@/features/warehouse/OutboundClient";
import { WarehousePicker, useWarehouseChoice } from "@/features/warehouse/WarehouseParts";
import { listOutbound, outboundKeys, type OutboundView } from "@/features/warehouse/outbound.api";

// Warehouse → Orders (docs/design_handoff_wms_mw8/README.md §5): the outbound orders of a warehouse
// and, behind the switch, the shipping form per address per day. Reading is wms_outbound; linking an
// address, cancelling and choosing a form wms_outbound_write — in the screen, and on the API regardless.

interface OutboundSearch {
  warehouse?: string;
  tab?: OutboundTab;
  view?: OutboundView;
  q?: string;
  /** De week: a day in the week shown (empty: this week), and the day chosen in it. */
  week?: string;
  day?: string;
}

export const Route = createFileRoute("/_shell/warehouse/outbound/")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "wms_outbound") && !hasModule(context.session, "wms_outbound_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): OutboundSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    const tab = str(search.tab);
    const view = str(search.view);
    const date = (v: unknown) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);
    return {
      warehouse: str(search.warehouse),
      // Verzending was a tab; it is in the week now, so an old link to it opens the orders.
      tab: tab === "changes" ? tab : undefined,
      view: view === "today" || view === "open" || view === "recent" ? view : undefined,
      q: str(search.q),
      week: date(search.week),
      day: date(search.day),
    };
  },
  component: OutboundScreen,
});

function OutboundScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const canEdit = session.roles.includes("admin") || hasModule(session, "wms_outbound_write");
  const view: OutboundView = search.view ?? "week";

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  const choice = useWarehouseChoice(search.warehouse, (code) => updateParams({ warehouse: code, q: "" }));
  const warehouse = choice.current;

  const query = useQuery({
    queryKey: outboundKeys.list(warehouse?.id ?? "", view, search.q ?? "", search.week ?? ""),
    queryFn: () => listOutbound(warehouse!.id, view, search.q ?? "", search.week ?? ""),
    enabled: warehouse !== undefined && search.tab === undefined,
    placeholderData: keepPreviousData,
  });

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Warehouse Orders">
      {choice.error ? (
        <div className="wl-alert wl-alert--danger">
          <span>{choice.error instanceof Error ? choice.error.message : t("common.errorGeneric")}</span>
        </div>
      ) : !warehouse ? (
        <p className="t-body-sm t-muted">{choice.isLoading ? t("common.loading") : t("warehouse.noWarehouses")}</p>
      ) : (
        <OutboundClient
          warehouse={warehouse}
          picker={<WarehousePicker warehouses={choice.warehouses} current={warehouse} onChoose={choice.choose} />}
          tab={search.tab ?? "orders"}
          view={view}
          q={search.q ?? ""}
          readyBy={query.data?.readyBy ?? null}
          weekMeta={query.data && view === "week" ? { outboundDay: query.data.outboundDay, today: query.data.today, week: query.data.week, readyBy: query.data.readyBy } : null}
          day={search.day ?? ""}
          rows={query.data?.orders ?? []}
          isLoading={query.isLoading}
          canEdit={canEdit}
          userId={session.userId}
          onTab={(tab) => updateParams({ tab: tab === "orders" ? "" : tab })}
          onView={(next) => updateParams({ view: next === "week" ? "" : next })}
          onWeek={(week) => updateParams({ week, day: "" })}
          onDay={(day) => updateParams({ day })}
          onSearch={(q) => updateParams({ q })}
        />
      )}
    </div>
  );
}
